/* This inserts error message DOM elements based on input elements with a data-errors attribute.
 * It expects a data-errors attribute in the shape of:
 *
 *   { "E-mail": ["has been taken", "is too short"] }
 *
 * Messages like: "E-mail has been taken; E-mail is too short" are generated and placed under the
 * closest parent element with a '.field' class (Bulma standard DOM structure).
 *
 * If the element has class "verbatim-error-message", the string is not prefixed with the attribute
 * name. */
import tippy, { sticky } from "tippy.js";
import { escape } from "lodash-es";

export const displayError = (target, content, { show = true } = {}) => {
  const tip = tippy(target, {
    content,
    theme: "error",
    allowHTML: true,
    sticky: true,
    plugins: [sticky],
  });
  if (show) tip.show();
};

const processInput = (input) => {
  clearTimeout(input._fieldErrorTimeout);
  input._fieldErrorTarget?._tippy?.destroy();
  if (!input.dataset.errors) return;

  const errors = JSON.parse(input.dataset.errors);
  const content = Object.entries(errors)
    .map(([, messages]) => messages.map((m) => escape(m)).join("<br>"))
    .flat()
    .join("\n");

  const target = ["checkbox", "radio"].includes(input.type)
    ? (input.closest("label") ?? input)
    : input;

  const show = !input.closest("[data-errors-quiet]");

  input._fieldErrorTimeout = setTimeout(() => displayError(target, content, { show }), 50);
  input._fieldErrorTarget = target;
};

const isEnabled = (input) => {
  const form = input.closest("form");
  return (
    form &&
    !form.matches("[data-inline-errors=false]") &&
    !input.matches("[data-inline-errors=false]")
  );
};

const scan = (container) => {
  /* Allow the behavior to be disabled at the form or input level */
  const selector =
    "form:not([data-inline-errors=false]) [data-errors]:not([data-inline-errors=false])";

  container.querySelectorAll(selector).forEach(processInput);
};

document.addEventListener("DOMContentLoaded", () => {
  scan(document);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === "data-errors" && isEnabled(mutation.target))
        processInput(mutation.target);
    });
  }).observe(document.body, { attributes: true, attributeFilter: ["data-errors"], subtree: true });
});

document.addEventListener("partial:replace", (ev) => scan(ev.container));

/* Vue directive to ensure components mounted with data-errors get set-up properly */
export const vFieldErrors = {
  mounted(el) {
    if (isEnabled(el)) processInput(el);
  },
  unmounted(el) {
    clearTimeout(el._fieldErrorTimeout);
    el._fieldErrorTarget?._tippy?.destroy();
  },
};
